import React, { useEffect, useState } from "react";
import { Sparkles, Loader2, Wallet, Image as ImageIcon, Wand2, Download, AlertCircle, Cpu } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";

const KIND_OPTIONS = [
  { value: "logo", label: "Logo" },
  { value: "image", label: "Hero / Banner / Image" },
];
const COMPLEXITY_OPTIONS = [
  { value: "simple", label: "Simple", cost: { logo: 1, image: 1 } },
  { value: "medium", label: "Medium", cost: { logo: 5, image: 5 } },
  { value: "complex", label: "Complex", cost: { logo: 15, image: 15 } },
];

export default function AIImages() {
  const [prompt, setPrompt] = useState("");
  const [kind, setKind] = useState("image");
  const [complexity, setComplexity] = useState("simple");
  const [provider, setProvider] = useState("");
  const [providers, setProviders] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [lastImages, setLastImages] = useState([]);

  const load = async () => {
    try {
      const [w, p, j] = await Promise.all([
        api.get("/credits/me").then(r => r.data),
        api.get("/ai/images/provider").then(r => r.data),
        api.get("/ai/images/me").then(r => r.data),
      ]);
      setWallet(w); setProviders(p); setJobs(j);
      if (!provider) setProvider(p.provider);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const cost = COMPLEXITY_OPTIONS.find(c => c.value === complexity)?.cost?.[kind] ?? 1;
  const insufficient = wallet && wallet.balance < cost;

  const generate = async () => {
    if (prompt.trim().length < 4) { toast.error("Prompt must be at least 4 characters"); return; }
    if (insufficient) { toast.error("Not enough credits — top up your wallet"); return; }
    setGenerating(true); setLastImages([]);
    try {
      const { data } = await api.post("/ai/images/generate", {
        prompt, kind, complexity, provider, n: 1,
      });
      setLastImages(data.images || []);
      setWallet(w => ({ ...(w || {}), balance: data.balance }));
      toast.success(`Generated · ${data.cost_credits} credits used · balance ${data.balance}`);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.detail || "Generation failed"));
    } finally { setGenerating(false); }
  };

  const backend = process.env.REACT_APP_BACKEND_URL;
  const fullUrl = (u) => (u && u.startsWith("http")) ? u : `${backend}${u}`;

  return (
    <div className="space-y-6" data-testid="ai-images-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="tag mb-2">AI Image Studio</div>
          <h1 className="text-3xl font-extrabold text-white flex items-center gap-3" style={{ fontFamily: "Outfit" }}>
            <Sparkles size={26} className="text-blue-400" /> Generate logos &amp; images
          </h1>
          <p className="text-slate-400 text-sm">Powered by Gemini Nano Banana &amp; OpenAI GPT-Image-1. Credits debit only on success.</p>
        </div>
        <Link to="/dashboard/credits" className="card px-4 py-3 inline-flex items-center gap-3 hover:border-blue-500/40 transition" data-testid="balance-pill">
          <Wallet size={18} className="text-blue-400" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Balance</div>
            <div className="text-xl font-extrabold text-white" style={{ fontFamily: "Outfit" }}>{wallet?.balance ?? "—"} cr</div>
          </div>
        </Link>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Generator */}
        <div className="card p-5 space-y-4">
          <h3 className="text-white font-semibold flex items-center gap-2"><Wand2 size={16} className="text-blue-400" /> Generator</h3>
          <div>
            <label className="label">What do you want?</label>
            <textarea
              data-testid="prompt-input"
              rows="5" className="input"
              value={prompt} onChange={e => setPrompt(e.target.value)}
              placeholder="e.g. A minimal logo for a botanical skincare brand called Velora — green leaf icon with serif wordmark"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Type</label>
              <select className="input" value={kind} onChange={e => setKind(e.target.value)} data-testid="kind-select">
                {KIND_OPTIONS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Complexity</label>
              <select className="input" value={complexity} onChange={e => setComplexity(e.target.value)} data-testid="complexity-select">
                {COMPLEXITY_OPTIONS.map(c => (
                  <option key={c.value} value={c.value}>{c.label} · {c.cost[kind]} cr</option>
                ))}
              </select>
            </div>
          </div>
          {providers && (
            <div>
              <label className="label flex items-center gap-1"><Cpu size={12} /> Provider</label>
              <select className="input" value={provider} onChange={e => setProvider(e.target.value)} data-testid="provider-select">
                {providers.options.map(p => <option key={p} value={p}>{providers.labels[p]}</option>)}
              </select>
            </div>
          )}
          {insufficient && (
            <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-3 text-sm text-rose-300 flex items-start gap-2" data-testid="insufficient-alert">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              <div>Not enough credits ({wallet.balance}/{cost}). <Link to="/dashboard/credits" className="underline">Top up</Link> to continue.</div>
            </div>
          )}
          <button
            disabled={generating || insufficient || prompt.trim().length < 4}
            onClick={generate}
            data-testid="generate-btn"
            className="btn-primary w-full inline-flex items-center justify-center gap-2"
          >
            {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {generating ? "Generating… (this can take ~30s)" : `Generate · ${cost} credit${cost > 1 ? "s" : ""}`}
          </button>
        </div>

        {/* Output */}
        <div className="card p-5">
          <h3 className="text-white font-semibold flex items-center gap-2 mb-3"><ImageIcon size={16} className="text-blue-400" /> Output</h3>
          {lastImages.length === 0 && !generating && (
            <div className="aspect-square bg-slate-900/60 rounded-xl border-2 border-dashed border-slate-700 flex flex-col items-center justify-center text-slate-500 text-sm">
              <ImageIcon size={48} className="opacity-30 mb-2" />
              <div>Your image will appear here</div>
            </div>
          )}
          {generating && (
            <div className="aspect-square bg-slate-900/60 rounded-xl flex flex-col items-center justify-center text-slate-400 text-sm">
              <Loader2 size={36} className="animate-spin text-blue-400 mb-3" />
              <div>Generating with {providers?.labels?.[provider] || provider}…</div>
            </div>
          )}
          {lastImages.map(img => (
            <div key={img.id} className="space-y-2" data-testid={`output-${img.id}`}>
              <img src={fullUrl(img.url)} alt="generated" className="w-full rounded-xl border border-slate-700" />
              <a href={fullUrl(img.url)} download className="btn-ghost inline-flex items-center gap-2 text-sm">
                <Download size={14} /> Download PNG
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* Recent gallery */}
      {jobs.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-xl font-bold text-white" style={{ fontFamily: "Outfit" }}>Recent generations</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="recent-gallery">
            {jobs.slice(0, 24).flatMap(j => (j.images || []).map(img => (
              <a key={img.id} href={fullUrl(img.url)} target="_blank" rel="noreferrer" className="relative group rounded-xl overflow-hidden border border-slate-700 hover:border-blue-500 transition">
                <img src={fullUrl(img.url)} alt={j.prompt} className="w-full aspect-square object-cover" />
                <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition flex items-end p-2">
                  <div className="text-[10px] text-slate-200 leading-tight line-clamp-3">{j.prompt}</div>
                </div>
              </a>
            )))}
          </div>
        </div>
      )}
    </div>
  );
}
